import { z } from 'zod';
import {
  acceptedSchema,
  loginSchema,
  meSchema,
  passwordResetConfirmSchema,
  passwordResetRequestSchema,
  registerSchema,
  sessionInfoSchema,
  switchWorkspaceSchema,
  tokenBodySchema,
} from './auth';
import {
  acceptInvitationSchema,
  changeRoleSchema,
  createInvitationSchema,
  invitationPreviewSchema,
  invitationSchema,
  memberSchema,
} from './workspace';
import {
  customerInputSchema,
  customerListQuerySchema,
  customerOptionSchema,
  customerSchema,
  customerUpdateSchema,
} from './customers';
import {
  jiraConnectionInputSchema,
  jiraConnectionSchema,
  jiraSiteSchema,
  jiraTestResultSchema,
} from './jira';
import {
  bizcycleProjectSchema,
  issueJobStateSchema,
  jiraIssueApproveSchema,
  jiraIssueSchema,
  jiraIssueUpdateSchema,
} from './bizcycle';
import { listResultSchema } from './list';
import {
  jiraAuthorSchema,
  jiraJobStateSchema,
  worklogListQuerySchema,
  worklogListSchema,
  worklogLoadInputSchema,
  worklogResolveResultSchema,
  worklogReviewSchema,
  worklogSchema,
} from './worklogs';
import {
  noteArchiveSchema,
  noteInputSchema,
  noteListQuerySchema,
  noteSchema,
  noteUpdateSchema,
} from './notes';
import { apiTokenCreatedSchema, apiTokenInputSchema, apiTokenSchema } from './api-tokens';
import { preferenceSchema } from './preferences';
import { productInputSchema, productSchema, productUpdateSchema } from './products';
import {
  projectInputSchema,
  projectListQuerySchema,
  projectOptionSchema,
  projectSchema,
  projectUpdateSchema,
} from './projects';
import { resourceInputSchema, resourceSchema, resourceUpdateSchema } from './resources';
import {
  taskDoneSchema,
  taskInputSchema,
  taskListQuerySchema,
  taskListSchema,
  taskSchema,
  taskUpdateSchema,
} from './tasks';
import { overviewQuerySchema, overviewSchema, roadmapQuerySchema, roadmapSchema } from './overview';
import {
  supportGapsSchema,
  supportInputSchema,
  supportListQuerySchema,
  supportSchema,
  supportUpdateSchema,
} from './support';
import { projectTimesheetsSchema, timesheetInputSchema, timesheetSchema } from './timesheets';
import { effectiveHoursSchema, timeOverviewQuerySchema, timeOverviewSchema } from './time-overview';
import {
  turnoverAnnotationInputSchema,
  turnoverAnnotationSchema,
  turnoverQuerySchema,
  turnoverSchema,
} from './turnover';
import {
  copyTechnologiesResultSchema,
  copyTechnologiesSchema,
  projectTechnologiesSchema,
  technologyInputSchema,
  technologySchema,
  technologyUpdateSchema,
} from './technologies';

/**
 * API route catalogue — one definition used by the NestJS controllers (paths) and by the typed
 * web client (paths + body/query/response types). No URL string is written anywhere else.
 */

export const API_PREFIX = '/api';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RouteDef {
  readonly method: HttpMethod;
  readonly path: string;
  readonly body?: z.ZodType;
  readonly query?: z.ZodType;
  readonly response?: z.ZodType;
}

const route = <const R extends RouteDef>(r: R): R => r;

export const routes = {
  health: route({ method: 'GET', path: '/health' }),
  auth: {
    register: route({
      method: 'POST',
      path: '/auth/register',
      body: registerSchema,
      response: acceptedSchema,
    }),
    verifyEmail: route({
      method: 'POST',
      path: '/auth/verify-email',
      body: tokenBodySchema,
      response: acceptedSchema,
    }),
    login: route({ method: 'POST', path: '/auth/login', body: loginSchema, response: meSchema }),
    logout: route({ method: 'POST', path: '/auth/logout', response: acceptedSchema }),
    me: route({ method: 'GET', path: '/auth/me', response: meSchema }),
    passwordResetRequest: route({
      method: 'POST',
      path: '/auth/password-reset/request',
      body: passwordResetRequestSchema,
      response: acceptedSchema,
    }),
    passwordResetConfirm: route({
      method: 'POST',
      path: '/auth/password-reset/confirm',
      body: passwordResetConfirmSchema,
      response: acceptedSchema,
    }),
    switchWorkspace: route({
      method: 'POST',
      path: '/auth/switch-workspace',
      body: switchWorkspaceSchema,
      response: meSchema,
    }),
    sessions: route({ method: 'GET', path: '/auth/sessions', response: z.array(sessionInfoSchema) }),
    revokeSession: route({ method: 'DELETE', path: '/auth/sessions/:id', response: acceptedSchema }),
  },
  members: {
    list: route({ method: 'GET', path: '/workspace/members', response: z.array(memberSchema) }),
    changeRole: route({
      method: 'PATCH',
      path: '/workspace/members/:userId',
      body: changeRoleSchema,
      response: memberSchema,
    }),
    remove: route({ method: 'DELETE', path: '/workspace/members/:userId', response: acceptedSchema }),
  },
  invitations: {
    list: route({ method: 'GET', path: '/workspace/invitations', response: z.array(invitationSchema) }),
    create: route({
      method: 'POST',
      path: '/workspace/invitations',
      body: createInvitationSchema,
      response: acceptedSchema,
    }),
    revoke: route({ method: 'DELETE', path: '/workspace/invitations/:id', response: acceptedSchema }),
    preview: route({ method: 'GET', path: '/invitations/:token', response: invitationPreviewSchema }),
    accept: route({
      method: 'POST',
      path: '/invitations/accept',
      body: acceptInvitationSchema,
      response: meSchema,
    }),
  },
  customers: {
    list: route({
      method: 'GET',
      path: '/customers',
      query: customerListQuerySchema,
      response: listResultSchema(customerSchema),
    }),
    create: route({
      method: 'POST',
      path: '/customers',
      body: customerInputSchema,
      response: customerSchema,
    }),
    options: route({ method: 'GET', path: '/customers/options', response: z.array(customerOptionSchema) }),
    get: route({ method: 'GET', path: '/customers/:id', response: customerSchema }),
    update: route({
      method: 'PATCH',
      path: '/customers/:id',
      body: customerUpdateSchema,
      response: customerSchema,
    }),
    remove: route({ method: 'DELETE', path: '/customers/:id', response: acceptedSchema }),
  },
  products: {
    list: route({ method: 'GET', path: '/customers/:id/products', response: z.array(productSchema) }),
    create: route({
      method: 'POST',
      path: '/customers/:id/products',
      body: productInputSchema,
      response: productSchema,
    }),
    update: route({
      method: 'PATCH',
      path: '/products/:id',
      body: productUpdateSchema,
      response: productSchema,
    }),
    remove: route({ method: 'DELETE', path: '/products/:id', response: acceptedSchema }),
  },
  resources: {
    list: route({ method: 'GET', path: '/resources', response: z.array(resourceSchema) }),
    get: route({ method: 'GET', path: '/resources/:id', response: resourceSchema }),
    create: route({
      method: 'POST',
      path: '/resources',
      body: resourceInputSchema,
      response: resourceSchema,
    }),
    update: route({
      method: 'PATCH',
      path: '/resources/:id',
      body: resourceUpdateSchema,
      response: resourceSchema,
    }),
    remove: route({ method: 'DELETE', path: '/resources/:id', response: acceptedSchema }),
  },
  projects: {
    list: route({
      method: 'GET',
      path: '/projects',
      query: projectListQuerySchema,
      response: listResultSchema(projectSchema),
    }),
    /** Pickers (task form): every project, open first (FR-TASK-016). */
    options: route({ method: 'GET', path: '/projects/options', response: z.array(projectOptionSchema) }),
    /** Open projects without a single task (FR-TASK-019). */
    withoutTasks: route({
      method: 'GET',
      path: '/projects/without-tasks',
      response: z.array(projectOptionSchema),
    }),
    create: route({ method: 'POST', path: '/projects', body: projectInputSchema, response: projectSchema }),
    get: route({ method: 'GET', path: '/projects/:id', response: projectSchema }),
    update: route({
      method: 'PATCH',
      path: '/projects/:id',
      body: projectUpdateSchema,
      response: projectSchema,
    }),
    remove: route({ method: 'DELETE', path: '/projects/:id', response: acceptedSchema }),
  },
  notes: {
    listForProject: route({
      method: 'GET',
      path: '/projects/:id/notes',
      query: noteListQuerySchema,
      response: z.array(noteSchema),
    }),
    createForProject: route({
      method: 'POST',
      path: '/projects/:id/notes',
      body: noteInputSchema,
      response: noteSchema,
    }),
    listForCustomer: route({
      method: 'GET',
      path: '/customers/:id/notes',
      query: noteListQuerySchema,
      response: z.array(noteSchema),
    }),
    createForCustomer: route({
      method: 'POST',
      path: '/customers/:id/notes',
      body: noteInputSchema,
      response: noteSchema,
    }),
    update: route({ method: 'PATCH', path: '/notes/:id', body: noteUpdateSchema, response: noteSchema }),
    setArchived: route({
      method: 'PATCH',
      path: '/notes/:id/archived',
      body: noteArchiveSchema,
      response: noteSchema,
    }),
    remove: route({ method: 'DELETE', path: '/notes/:id', response: acceptedSchema }),
  },
  technologies: {
    list: route({ method: 'GET', path: '/technologies', response: z.array(technologySchema) }),
    create: route({
      method: 'POST',
      path: '/technologies',
      body: technologyInputSchema,
      response: technologySchema,
    }),
    update: route({
      method: 'PATCH',
      path: '/technologies/:id',
      body: technologyUpdateSchema,
      response: technologySchema,
    }),
    remove: route({ method: 'DELETE', path: '/technologies/:id', response: acceptedSchema }),
    ofProject: route({
      method: 'GET',
      path: '/projects/:id/technologies',
      response: projectTechnologiesSchema,
    }),
    setForProject: route({
      method: 'PUT',
      path: '/projects/:id/technologies',
      body: projectTechnologiesSchema,
      response: projectTechnologiesSchema,
    }),
    copyToProjects: route({
      method: 'POST',
      path: '/projects/:id/technologies/copy',
      body: copyTechnologiesSchema,
      response: copyTechnologiesResultSchema,
    }),
  },
  tasks: {
    list: route({ method: 'GET', path: '/tasks', query: taskListQuerySchema, response: taskListSchema }),
    get: route({ method: 'GET', path: '/tasks/:id', response: taskSchema }),
    create: route({ method: 'POST', path: '/tasks', body: taskInputSchema, response: taskSchema }),
    update: route({ method: 'PATCH', path: '/tasks/:id', body: taskUpdateSchema, response: taskSchema }),
    setDone: route({ method: 'PATCH', path: '/tasks/:id/done', body: taskDoneSchema, response: taskSchema }),
    remove: route({ method: 'DELETE', path: '/tasks/:id', response: acceptedSchema }),
  },
  timesheets: {
    /** Plan and Fact of a project; a sheet never saved comes back as the default (FR-TS-002). */
    ofProject: route({ method: 'GET', path: '/projects/:id/timesheets', response: projectTimesheetsSchema }),
    /** Saves one scenario (`plan` / `fact`) as a whole, with its version (FR-TS-013). */
    save: route({
      method: 'PUT',
      path: '/projects/:id/timesheets/:scenario',
      body: timesheetInputSchema,
      response: timesheetSchema,
    }),
  },
  support: {
    list: route({
      method: 'GET',
      path: '/support',
      query: supportListQuerySchema,
      response: z.array(supportSchema),
    }),
    gaps: route({ method: 'GET', path: '/support/gaps', response: supportGapsSchema }),
    create: route({ method: 'POST', path: '/support', body: supportInputSchema, response: supportSchema }),
    update: route({
      method: 'PATCH',
      path: '/support/:id',
      body: supportUpdateSchema,
      response: supportSchema,
    }),
    remove: route({ method: 'DELETE', path: '/support/:id', response: acceptedSchema }),
  },
  overview: {
    /** Projects started in the period with their indicators (FR-OVR-001…007). */
    get: route({ method: 'GET', path: '/overview', query: overviewQuerySchema, response: overviewSchema }),
    /** Monthly revenue per project (FR-OVR-013…018). */
    roadmap: route({ method: 'GET', path: '/roadmap', query: roadmapQuerySchema, response: roadmapSchema }),
  },
  turnover: {
    /** One month's turnover, annotations and outstanding invoices (FR-TURN-001…010). */
    get: route({ method: 'GET', path: '/turnover', query: turnoverQuerySchema, response: turnoverSchema }),
    /** Comment and/or "under question" flag of a project in a month (FR-TURN-006/007). */
    annotate: route({
      method: 'PUT',
      path: '/projects/:id/turnover-annotation',
      body: turnoverAnnotationInputSchema,
      response: turnoverAnnotationSchema,
    }),
  },
  jira: {
    /** Settings page (owner/admin, FR-JIRA-001, FR-WS-011). */
    connection: route({ method: 'GET', path: '/jira/connection', response: jiraConnectionSchema }),
    saveConnection: route({
      method: 'PUT',
      path: '/jira/connection',
      body: jiraConnectionInputSchema,
      response: jiraConnectionSchema,
    }),
    testConnection: route({ method: 'POST', path: '/jira/connection/test', response: jiraTestResultSchema }),
    /** Forgets the token; URL and e-mail stay (FR-JIRA-005). */
    disconnect: route({ method: 'DELETE', path: '/jira/connection', response: jiraConnectionSchema }),
    /** Base URL for links to Jira — any member (FR-PRJ-015). */
    site: route({ method: 'GET', path: '/jira/site', response: jiraSiteSchema }),
    /** Distinct worklog authors for the resource's Jira account (FR-RES-005). */
    authors: route({ method: 'GET', path: '/jira/authors', response: z.array(jiraAuthorSchema) }),
  },
  timeOverview: {
    /** Approved/active worklog hours by person or project and period (FR-OVR-008…012). */
    get: route({
      method: 'GET',
      path: '/time-overview',
      query: timeOverviewQuerySchema,
      response: timeOverviewSchema,
    }),
    /** Approved worklogs of a project by resource and month (FR-TS-020…023). */
    effective: route({
      method: 'GET',
      path: '/projects/:id/effective-hours',
      response: effectiveHoursSchema,
    }),
  },
  bizcycle: {
    /** Open Jira tasks of the workspace (FR-BIZ-007…016). */
    issues: route({ method: 'GET', path: '/bizcycle/issues', response: z.array(jiraIssueSchema) }),
    /** Live projects with client and technologies (FR-BIZ-011/012/017). */
    projects: route({ method: 'GET', path: '/bizcycle/projects', response: z.array(bizcycleProjectSchema) }),
    /** Starts the background task sync (FR-BIZ-002…004); 409 BUSY / NOTHING_TO_SYNC. */
    sync: route({ method: 'POST', path: '/bizcycle/sync', response: issueJobStateSchema }),
    syncState: route({ method: 'GET', path: '/bizcycle/sync', response: issueJobStateSchema }),
    update: route({
      method: 'PATCH',
      path: '/bizcycle/issues/:key',
      body: jiraIssueUpdateSchema,
      response: jiraIssueSchema,
    }),
    approve: route({
      method: 'POST',
      path: '/bizcycle/issues/approve',
      body: jiraIssueApproveSchema,
      response: z.object({ approved: z.number().int() }),
    }),
  },
  worklogs: {
    /** A tab of the reconciliation page with filters, counts and the load state (FR-JIRA-013…017). */
    list: route({
      method: 'GET',
      path: '/jira/worklogs',
      query: worklogListQuerySchema,
      response: worklogListSchema,
    }),
    /** Starts a background load (incremental or full) — 409 BUSY if one runs (FR-JIRA-007/008). */
    load: route({
      method: 'POST',
      path: '/jira/worklogs/load',
      body: worklogLoadInputSchema,
      response: jiraJobStateSchema,
    }),
    /** The load in progress or the last one. */
    state: route({ method: 'GET', path: '/jira/worklogs/state', response: jiraJobStateSchema }),
    /** Auto-maps again every worklog not mapped by hand (FR-JIRA-012). */
    resolve: route({ method: 'POST', path: '/jira/worklogs/resolve', response: worklogResolveResultSchema }),
    review: route({
      method: 'PATCH',
      path: '/jira/worklogs/:id',
      body: worklogReviewSchema,
      response: worklogSchema,
    }),
  },
  /** The signed-in user's API tokens in the active workspace (FR-WS-009) — session only. */
  apiTokens: {
    list: route({ method: 'GET', path: '/workspace/api-tokens', response: z.array(apiTokenSchema) }),
    create: route({
      method: 'POST',
      path: '/workspace/api-tokens',
      body: apiTokenInputSchema,
      response: apiTokenCreatedSchema,
    }),
    revoke: route({ method: 'DELETE', path: '/workspace/api-tokens/:id', response: acceptedSchema }),
  },
  /** MCP Streamable HTTP (JSON-RPC over POST) with `Authorization: Bearer <API token>` (FR-MCP-011). */
  mcp: route({ method: 'POST', path: '/mcp' }),
  preferences: {
    get: route({ method: 'GET', path: '/preferences/:key', response: preferenceSchema }),
    put: route({
      method: 'PUT',
      path: '/preferences/:key',
      body: preferenceSchema,
      response: preferenceSchema,
    }),
  },
} as const;

export type BodyOf<R extends RouteDef> = R['body'] extends z.ZodType ? z.input<R['body']> : undefined;
export type QueryOf<R extends RouteDef> = R['query'] extends z.ZodType ? z.input<R['query']> : undefined;
export type ResponseOf<R extends RouteDef> = R['response'] extends z.ZodType
  ? z.output<R['response']>
  : unknown;

/** `/customers/:id` + `{ id: '5' }` → `/customers/5` (values are URI-encoded). */
export function buildPath(path: string, params: Readonly<Record<string, string>> = {}): string {
  return path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(value);
  });
}

/** The `:name` parameters of a path, in order. */
export const pathParams = (path: string): string[] => [...path.matchAll(/:([A-Za-z]+)/g)].map((m) => m[1]!);

/** Query object → `?a=1&b=x,y` (arrays comma-joined, empty values left out) — as `queryList` reads it. */
export function buildQuery(query: object | undefined): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}
